import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Upload, 
  CheckCircle, 
  ArrowRight,
  AlertTriangle,
  Sparkles,
  Languages,
  TriangleAlert,
  Clock,
  Lock,
  Play,
  Star,
  Users,
  Zap,
  Award,
  TrendingUp,
  Shield,
  Target,
  BarChart3,
  Dribbble,
  CircleDot,
  Volleyball,
  Bike,
  Dumbbell,
  Swords,
  Footprints,
  Trophy
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/hero-athletes-action.jpg";
import logo from "@/assets/logo.png";
import MobileNav from "@/components/MobileNav";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import { SUBSCRIPTION_TIERS } from "@/contexts/SubscriptionContext";

const Index = () => {
  const { t } = useTranslation();
  
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const sports = [
    { name: "Basketball", icon: Dribbble, leagues: "NBA, EuroLeague, WNBA" },
    { name: "Football", icon: Trophy, leagues: "NFL, CFL, XFL" },
    { name: "Soccer", icon: CircleDot, leagues: "Premier League, MLS, La Liga" },
    { name: "Baseball", icon: Target, leagues: "MLB, NPB, KBO" },
    { name: "Hockey", icon: Swords, leagues: "NHL, KHL, AHL" },
    { name: "Tennis", icon: Volleyball, leagues: "ATP, WTA, ITF" },
  ];

  const features = [
    {
      icon: Sparkles,
      titleKey: "landing.instantAnalysis",
      descKey: "landing.instantAnalysisDesc",
      gradient: "from-accent/20 to-accent/5",
      iconBg: "bg-accent/10",
      iconColor: "text-accent"
    },
    {
      icon: Languages,
      titleKey: "landing.plainEnglish",
      descKey: "landing.plainEnglishDesc",
      gradient: "from-secondary/20 to-secondary/5",
      iconBg: "bg-secondary/10",
      iconColor: "text-secondary"
    },
    {
      icon: TriangleAlert,
      titleKey: "landing.riskDetection",
      descKey: "landing.riskDetectionDesc",
      gradient: "from-destructive/20 to-destructive/5",
      iconBg: "bg-destructive/10",
      iconColor: "text-destructive"
    },
    {
      icon: TrendingUp,
      titleKey: "landing.marketComparison",
      descKey: "landing.marketComparisonDesc",
      gradient: "from-primary/20 to-primary/5",
      iconBg: "bg-primary/10",
      iconColor: "text-primary"
    },
    {
      icon: FileText,
      titleKey: "landing.counterProposals",
      descKey: "landing.counterProposalsDesc",
      gradient: "from-accent/20 to-accent/5",
      iconBg: "bg-accent/10",
      iconColor: "text-accent"
    },
    {
      icon: Lock,
      titleKey: "landing.hundredPercentSecure",
      descKey: "landing.secureDesc",
      gradient: "from-success/20 to-success/5",
      iconBg: "bg-success/10",
      iconColor: "text-success"
    }
  ];

  const steps = [
    {
      step: 1,
      icon: Upload,
      titleKey: "landing.step1Title",
      descKey: "landing.step1Desc",
      color: "secondary"
    },
    {
      step: 2,
      icon: CheckCircle,
      titleKey: "landing.step2Title",
      descKey: "landing.step2Desc",
      color: "accent"
    },
    {
      step: 3,
      icon: FileText,
      titleKey: "landing.step3Title",
      descKey: "landing.step3Desc",
      color: "primary"
    }
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-accent focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
      >
        Skip to main content
      </a>

      {/* Navigation */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex items-center gap-3 group" aria-label="Sports Deal Shield - Home">
              <div className="relative">
                <img src={logo} alt="" className="w-10 h-10 rounded-xl shadow-lg shadow-accent/25 group-hover:shadow-accent/40 transition-all duration-300 object-contain" aria-hidden="true" />
                <div className="absolute -inset-1 bg-gradient-to-r from-accent/20 to-secondary/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <span className="font-bold text-xl text-foreground tracking-tight">Sports Deal Shield</span>
            </Link>
            <div className="hidden lg:flex items-center gap-10" role="menubar">
              <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background rounded" role="menuitem">
                {t('landing.features')}
              </a>
              <a href="#sports" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background rounded" role="menuitem">
                {t('landing.sports')}
              </a>
              <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background rounded" role="menuitem">
                {t('landing.howItWorks')}
              </a>
              <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background rounded" role="menuitem">
                {t('landing.pricing')}
              </a>
              <Link to="/about" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background rounded" role="menuitem">
                {t('landing.about')}
              </Link>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden md:block">
                <LanguageSelector compact />
              </div>
              <ThemeToggle />
              <Button variant="ghost" asChild className="hidden sm:flex text-muted-foreground hover:text-foreground hover:bg-muted focus:ring-2 focus:ring-accent">
                <Link to="/auth">{t('common.signIn')}</Link>
              </Button>
              <Button asChild className="hidden sm:flex bg-gradient-to-r from-accent to-accent/80 hover:from-accent/90 hover:to-accent/70 text-accent-foreground shadow-lg shadow-accent/25 hover:shadow-accent/40 transition-all duration-300 focus:ring-2 focus:ring-accent focus:ring-offset-2">
                <Link to="/auth">{t('common.getStarted')}</Link>
              </Button>
              <MobileNav links={[
                { href: "#features", label: t('landing.features'), isAnchor: true },
                { href: "#sports", label: t('landing.sports'), isAnchor: true },
                { href: "#how-it-works", label: t('landing.howItWorks'), isAnchor: true },
                { href: "#pricing", label: t('landing.pricing'), isAnchor: true },
                { href: "/about", label: t('landing.about') },
                { href: "/contact", label: t('landing.contact') },
              ]} />
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section - Reduced top padding to eliminate dead zone */}
      <section className="relative overflow-hidden bg-background pt-20" id="main-content">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[100px]" />
          <div className="absolute top-1/3 -left-40 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[80px]" />
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:60px_60px] opacity-[0.03]" />
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Column - Content */}
            <motion.div 
              className="space-y-6 text-center lg:text-left"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              {/* Multi-sport badge */}
              <motion.div 
                className="inline-flex items-center gap-3 bg-muted/50 backdrop-blur-sm border border-border rounded-full px-5 py-2.5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex -space-x-1">
                  <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                    <Dribbble className="h-2.5 w-2.5 text-white" />
                  </div>
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <CircleDot className="h-2.5 w-2.5 text-white" />
                  </div>
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                    <Trophy className="h-2.5 w-2.5 text-white" />
                  </div>
                  <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                    <Target className="h-2.5 w-2.5 text-white" />
                  </div>
                </div>
                <span className="text-sm text-foreground font-medium">{t('landing.allMajorSports')}</span>
              </motion.div>

              <div className="space-y-4">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-[1.1] tracking-tight">
                  {t('landing.understandContract')}
                  <br />
                  <span className="bg-gradient-to-r from-accent via-secondary to-accent bg-clip-text text-transparent bg-[length:200%_100%]">
                    {t('landing.sportsContract')}
                  </span>
                  <br />
                  <span className="text-foreground/90">{t('landing.beforeYouSign')}</span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 leading-relaxed px-2 sm:px-0">
                  {t('landing.heroDescription')}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start px-2 sm:px-0">
                <Button 
                  size="lg" 
                  asChild 
                  className="bg-gradient-to-r from-accent to-accent/80 hover:from-accent hover:to-accent text-accent-foreground text-sm sm:text-base px-6 sm:px-8 h-12 sm:h-14 gap-2 sm:gap-3 shadow-xl shadow-accent/25 hover:shadow-accent/40 transition-all duration-300 rounded-xl group w-full sm:w-auto"
                >
                  <Link to="/auth">
                    <Upload className="h-4 w-4 sm:h-5 sm:w-5 group-hover:scale-110 transition-transform" />
                    <span className="whitespace-nowrap">{t('landing.analyzeContractFree')}</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform hidden sm:block" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  asChild 
                  className="border-border bg-muted/50 text-foreground hover:bg-muted hover:border-border text-sm sm:text-base px-6 sm:px-8 h-12 sm:h-14 gap-2 sm:gap-3 rounded-xl backdrop-blur-sm w-full sm:w-auto"
                >
                  <a href="#how-it-works">
                    <Play className="h-4 w-4 sm:h-5 sm:w-5" />
                    {t('landing.seeHowItWorks')}
                  </a>
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-4 sm:gap-x-6 gap-y-2 sm:gap-y-3 pt-2 px-2 sm:px-0">
                <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground">
                  <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-accent flex-shrink-0" />
                  <span className="text-xs sm:text-sm whitespace-nowrap">{t('landing.bankLevelSecurity')}</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground">
                  <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-accent flex-shrink-0" />
                  <span className="text-xs sm:text-sm whitespace-nowrap">{t('landing.twoMinuteAnalysis')}</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground">
                  <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-accent flex-shrink-0" />
                  <span className="text-xs sm:text-sm whitespace-nowrap">{t('landing.noCreditCard')}</span>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Hero Image with Floating Elements & Parallax */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            >
              <motion.div 
                className="relative"
                initial={{ y: 0 }}
                animate={{ y: [0, -10, 0] }}
                transition={{ 
                  duration: 6, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              >
                {/* Animated glow effect behind image */}
                <motion.div 
                  className="absolute -inset-4 bg-gradient-to-r from-accent/20 via-secondary/20 to-primary/20 rounded-[2rem] blur-2xl"
                  animate={{ 
                    opacity: [0.4, 0.6, 0.4],
                    scale: [1, 1.02, 1]
                  }}
                  transition={{ 
                    duration: 4, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                />
                
                {/* Main image container with subtle scale animation */}
                <motion.div 
                  className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl shadow-black/30 dark:shadow-black/50 border border-border"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.4 }}
                >
                  <motion.img 
                    src={heroImage} 
                    alt="Professional athletes from basketball, football, soccer, and tennis in action" 
                    className="w-full h-[280px] sm:h-[350px] md:h-[400px] lg:h-[520px] object-cover object-center"
                    fetchPriority="high"
                    width={1920}
                    height={1080}
                    decoding="async"
                    loading="eager"
                    initial={{ scale: 1.05 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-80" />
                  <div className="absolute inset-0 bg-gradient-to-r from-background/30 to-transparent" />
                </motion.div>
                
                {/* Floating Stats Card - Bottom — reconciled to "10K+ Athletes" */}
                <motion.div 
                  className="absolute -bottom-4 sm:-bottom-6 left-2 right-2 sm:left-4 sm:right-4 lg:-left-6 lg:right-auto lg:w-72"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <div className="bg-card/95 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-5 shadow-2xl border border-border">
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                      <div>
                        <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary">10K+</div>
                        <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1">Athletes</div>
                      </div>
                      <div className="border-x border-border">
                        <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary">&lt;2m</div>
                        <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1">{t('landing.analysis')}</div>
                      </div>
                      <div>
                        <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary">98%</div>
                        <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1">{t('landing.satisfied')}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Floating Sports badge - Top Right */}
                <motion.div 
                  className="absolute -top-4 -right-4 lg:right-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  <div className="bg-gradient-to-br from-accent to-accent/80 text-white rounded-2xl px-4 py-2 shadow-lg shadow-accent/30 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    <span className="text-sm font-semibold">{t('landing.sixPlusSports')}</span>
                  </div>
                </motion.div>

                {/* Floating Trust badge - Top Left */}
                <motion.div 
                  className="absolute top-4 -left-4 lg:left-4 hidden lg:block"
                  initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ duration: 0.5, delay: 1 }}
                >
                  <div className="bg-card/95 backdrop-blur-xl rounded-2xl p-3 shadow-lg border border-border flex items-center gap-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-warning fill-warning" />
                      ))}
                    </div>
                    <span className="text-xs font-medium text-muted-foreground">{t('landing.rating')}</span>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* USP Section - Accessibility for All Athletes */}
      <section className="py-16 lg:py-24 bg-muted/30 relative overflow-hidden">
        {/* Background accents */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-secondary/5 rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge variant="secondary" className="mb-4 px-4 py-1.5 bg-accent/10 text-accent border-accent/20">
              <Users className="h-3 w-3 mr-1" />
              {t('landing.levelingPlayingField')}
            </Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-foreground mb-4 lg:mb-6">
              {t('landing.professionalAnalysis')}
              <br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-accent via-secondary to-accent bg-clip-text text-transparent">
                {t('landing.withoutPriceTag')}
              </span>
            </h2>
            <p className="text-base lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {t('landing.uspDescription')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {/* Card 1 - Minor/Lower Leagues */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="relative p-6 lg:p-8 bg-card/50 border-border backdrop-blur-sm hover:bg-card transition-all duration-300 h-full group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent to-accent/50 rounded-t-lg" />
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-5 group-hover:bg-accent/20 transition-colors">
                  <Award className="h-7 w-7 text-accent" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">{t('landing.minorLeagues')}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t('landing.minorLeaguesDescription')}
                </p>
              </Card>
            </motion.div>

            {/* Card 2 - No Legal Representation */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="relative p-6 lg:p-8 bg-card/50 border-border backdrop-blur-sm hover:bg-card transition-all duration-300 h-full group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-secondary/50 rounded-t-lg" />
                <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-5 group-hover:bg-secondary/20 transition-colors">
                  <Shield className="h-7 w-7 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">{t('landing.noAgentNoProblem')}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t('landing.noAgentDescription')}
                </p>
              </Card>
            </motion.div>

            {/* Card 3 - Budget Conscious */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card className="relative p-6 lg:p-8 bg-card/50 border-border backdrop-blur-sm hover:bg-card transition-all duration-300 h-full group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary/50 rounded-t-lg" />
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                  <Target className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">{t('landing.protectFuture')}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t('landing.protectFutureDescription')}
                </p>
              </Card>
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            className="text-center mt-12 lg:mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-muted-foreground text-sm mb-4">{t('landing.joinAthletes')}</p>
            <Button 
              size="lg" 
              asChild 
              className="bg-gradient-to-r from-accent to-accent/80 hover:from-accent hover:to-accent text-accent-foreground px-8 h-14 gap-3 shadow-xl shadow-accent/25 hover:shadow-accent/40 transition-all duration-300 rounded-xl group"
            >
              <Link to="/auth">
                <span>{t('landing.startFreeAnalysis')}</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Sports Section */}
      <section id="sports" className="py-12 sm:py-16 lg:py-20 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            className="text-center mb-8 sm:mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge variant="secondary" className="mb-3 sm:mb-4 px-3 sm:px-4 py-1 sm:py-1.5 bg-secondary/10 text-secondary border-secondary/20 text-xs sm:text-sm">
              <Zap className="h-3 w-3 mr-1" />
              {t('landing.multiSportPlatform')}
            </Badge>
            <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold mb-2 sm:mb-3 px-2">{t('landing.builtForEveryAthlete')}</h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto px-4">
              {t('landing.sportsDescription')}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-6">
            {sports.map((sport, index) => (
              <motion.div
                key={sport.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="p-3 sm:p-4 lg:p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-border/50 bg-card/50 backdrop-blur-sm group cursor-pointer h-full">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 rounded-lg sm:rounded-xl bg-gradient-to-br from-accent/10 to-secondary/10 flex items-center justify-center group-hover:from-accent/20 group-hover:to-secondary/20 transition-colors">
                    <sport.icon className="h-5 w-5 sm:h-6 sm:w-6 text-accent" />
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base mb-0.5 sm:mb-1">{sport.name}</h3>
                  <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">{sport.leagues}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="secondary" className="mb-4 bg-accent/10 text-accent border-accent/20 px-4 py-1.5">
              <Sparkles className="h-3 w-3 mr-1" />
              {t('landing.powerfulFeatures')}
            </Badge>
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">
              {t('landing.everythingYouNeed')}
              <br className="hidden sm:block" />
              <span className="text-accent">{t('landing.protectYourCareer')}</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.featuresDescription')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <motion.div 
                key={feature.titleKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className={`p-6 lg:p-8 h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br ${feature.gradient} backdrop-blur-sm group hover:-translate-y-1`}>
                  <div className={`w-14 h-14 ${feature.iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className={`h-7 w-7 ${feature.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t(feature.titleKey)}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t(feature.descKey)}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge variant="secondary" className="mb-4 px-4 py-1.5">
              <Zap className="h-3 w-3 mr-1" />
              {t('landing.simpleProcess')}
            </Badge>
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">{t('landing.howItWorks')}</h2>
            <p className="text-lg text-muted-foreground">{t('landing.threeSimpleSteps')}</p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto relative">
            {/* Connection line - desktop only */}
            <div className="hidden lg:block absolute top-24 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-secondary via-accent to-primary" />
            
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="p-8 text-center space-y-5 shadow-lg hover:shadow-xl transition-all duration-300 border-0 relative overflow-hidden bg-card h-full hover:-translate-y-1">
                  <div className={`w-20 h-20 bg-${item.color}/10 rounded-full flex items-center justify-center mx-auto relative z-10`}>
                    <div className={`w-14 h-14 bg-${item.color} rounded-full flex items-center justify-center shadow-lg`}>
                      <item.icon className="h-7 w-7 text-white" />
                    </div>
                    <div className={`absolute -top-2 -right-2 w-8 h-8 bg-${item.color} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                      {item.step}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold">{t(item.titleKey)}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t(item.descKey)}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-primary via-primary/95 to-primary">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 text-center text-primary-foreground">
            {[
              { icon: Clock, value: "2 min", label: "Average Analysis Time" },
              { icon: Lock, value: "100%", label: "Confidential & Secure" },
              { icon: Users, value: "10K+", label: "Athletes Trust Us" },
              { icon: Award, value: "98%", label: "Satisfaction Rate" }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <stat.icon className="h-10 w-10 mx-auto mb-4 opacity-80" />
                <div className="text-3xl lg:text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-sm lg:text-base opacity-70">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison Section — moved immediately before pricing */}
      <section className="py-16 lg:py-24 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            className="text-center mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge variant="secondary" className="mb-4 px-4 py-1.5 bg-destructive/10 text-destructive border-destructive/20">
              <TrendingUp className="h-3 w-3 mr-1" />
              {t('costComparison.badge')}
            </Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4">
              {t('costComparison.title')}
              <br className="hidden sm:block" />
              <span className="text-accent"> {t('costComparison.titleHighlight')}</span>
            </h2>
            <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('costComparison.description')}
            </p>
          </motion.div>

          {/* Comparison Table */}
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Card className="overflow-hidden border-border/50 shadow-xl">
              <div className="grid grid-cols-3 bg-muted/50 border-b border-border">
                <div className="p-4 lg:p-6 font-semibold text-sm lg:text-base">{t('costComparison.service')}</div>
                <div className="p-4 lg:p-6 text-center font-semibold text-sm lg:text-base border-x border-border">
                  <span className="text-muted-foreground">{t('costComparison.traditionalLawyer')}</span>
                </div>
                <div className="p-4 lg:p-6 text-center font-semibold text-sm lg:text-base">
                  <span className="text-accent">{t('costComparison.sportsDealShield')}</span>
                </div>
              </div>

              {/* Table Rows */}
              {[
                { serviceKey: "costComparison.initialReview", lawyer: "$500 - $1,500", shieldKey: "costComparison.freeTier", savings: true },
                { serviceKey: "costComparison.fullAnalysis", lawyer: "$1,000 - $3,000", shield: "$19/month", savings: true },
                { serviceKey: "costComparison.counterProposalDraft", lawyer: "$750 - $2,000", shieldKey: "costComparison.included", savings: true },
                { serviceKey: "costComparison.riskAssessment", lawyer: "$400 - $800", shieldKey: "costComparison.included", savings: true },
                { serviceKey: "costComparison.salaryBenchmarking", lawyer: "$300 - $600", shieldKey: "costComparison.included", savings: true },
                { serviceKey: "costComparison.expertReview", lawyer: "$1,500 - $5,000", shield: "$99/offer", savings: true },
                { serviceKey: "costComparison.turnaroundTime", lawyerKey: "costComparison.days", shieldKey: "costComparison.minutes", time: true },
                { serviceKey: "costComparison.availability", lawyerKey: "costComparison.no", shieldKey: "costComparison.yes", check: true },
              ].map((row, index) => (
                <div 
                  key={row.serviceKey} 
                  className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-background' : 'bg-muted/20'} ${index < 7 ? 'border-b border-border/50' : ''}`}
                >
                  <div className="p-4 lg:p-5 text-sm lg:text-base font-medium flex items-center">
                    {t(row.serviceKey)}
                  </div>
                  <div className="p-4 lg:p-5 text-center text-sm lg:text-base border-x border-border/50 flex items-center justify-center">
                    <span className={row.check ? "text-destructive" : "text-muted-foreground"}>
                      {row.lawyerKey ? t(row.lawyerKey) : row.lawyer}
                    </span>
                  </div>
                  <div className="p-4 lg:p-5 text-center text-sm lg:text-base flex items-center justify-center">
                    <span className={`font-semibold ${row.check ? "text-success" : row.time ? "text-accent" : "text-accent"}`}>
                      {row.shieldKey ? t(row.shieldKey) : row.shield}
                    </span>
                  </div>
                </div>
              ))}

              {/* Total Savings Row */}
              <div className="grid grid-cols-3 bg-gradient-to-r from-accent/10 via-accent/5 to-accent/10 border-t-2 border-accent/30">
                <div className="p-5 lg:p-6 font-bold text-base lg:text-lg flex items-center">
                  {t('costComparison.typicalCost')}
                </div>
                <div className="p-5 lg:p-6 text-center border-x border-accent/20 flex items-center justify-center">
                  <span className="text-lg lg:text-2xl font-bold text-destructive line-through decoration-2">
                    $3,000+
                  </span>
                </div>
                <div className="p-5 lg:p-6 text-center flex items-center justify-center">
                  <div>
                    <span className="text-lg lg:text-2xl font-bold text-accent">$0 - $19</span>
                    <span className="text-xs lg:text-sm text-muted-foreground block mt-1">{t('costComparison.perMonthLabel')}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Savings Callout */}
            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <div className="inline-flex items-center gap-3 bg-success/10 border border-success/20 rounded-2xl px-6 py-4">
                <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-success" />
                </div>
                <div className="text-left">
                  <p className="text-sm text-muted-foreground">{t('costComparison.averageSavings')}</p>
                  <p className="text-2xl font-bold text-success">$2,976+ {t('costComparison.perContract')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing — canonical prices from SubscriptionContext + Free tier */}
      <section id="pricing" className="py-16 sm:py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div 
            className="text-center mb-10 sm:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge variant="secondary" className="mb-3 sm:mb-4 px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm">
              {t('pricing.transparentPricing')}
            </Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4">{t('pricing.simpleFairPricing')}</h2>
            <p className="text-base sm:text-lg text-muted-foreground">{t('pricing.choosePlan')}</p>
            <p className="text-sm text-accent mt-2 font-medium">Save 20% with Annual Billing</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {/* Free */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
            >
              <Card className="p-5 sm:p-6 space-y-4 sm:space-y-5 h-full border border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-4">
                    <Zap className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold mb-1">Free</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold">$0</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Get started — no credit card required</p>
                </div>
                <ul className="space-y-3">
                  {["5 Contract Analyses / Month", "Plain-English Summaries", "Basic Red Flag Detection", "Risk Tier Classification"].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full h-10 rounded-xl" asChild>
                  <Link to="/auth">Get Started Free</Link>
                </Button>
              </Card>
            </motion.div>

            {/* Basic — $19/mo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-5 sm:p-6 space-y-4 sm:space-y-5 h-full border border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                    <Shield className="h-6 w-6 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-1">Basic</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold">${SUBSCRIPTION_TIERS.basic.monthly_price}</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground">For regular contract reviews</p>
                </div>
                <ul className="space-y-3">
                  {SUBSCRIPTION_TIERS.basic.features.slice(0, 5).map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full h-10 rounded-xl" asChild>
                  <Link to="/pricing">Start 14-Day Trial</Link>
                </Button>
              </Card>
            </motion.div>

            {/* Pro — $49/mo — Featured */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-5 sm:p-6 space-y-4 sm:space-y-5 h-full border-2 border-accent relative shadow-2xl bg-gradient-to-b from-card to-accent/5">
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white px-3 py-1 text-xs">{t('pricing.mostPopular')}</Badge>
                <div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
                    <Award className="h-6 w-6 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-1">Pro</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold">${SUBSCRIPTION_TIERS.pro.monthly_price}</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground">For active athletes managing contracts</p>
                </div>
                <ul className="space-y-3">
                  {SUBSCRIPTION_TIERS.pro.features.slice(0, 6).map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full h-10 rounded-xl bg-accent hover:bg-accent/90 shadow-lg shadow-accent/25" asChild>
                  <Link to="/pricing">{t('pricing.startProTrial')}</Link>
                </Button>
              </Card>
            </motion.div>

            {/* Premium — $99/mo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card className="p-5 sm:p-6 space-y-4 sm:space-y-5 h-full border border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                    <Star className="h-6 w-6 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-1">Premium</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold">${SUBSCRIPTION_TIERS.premium.monthly_price}</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Complete toolkit for professionals</p>
                </div>
                <ul className="space-y-3">
                  {SUBSCRIPTION_TIERS.premium.features.slice(0, 6).map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full h-10 rounded-xl" asChild>
                  <Link to="/pricing">View All Plans</Link>
                </Button>
              </Card>
            </motion.div>
          </div>

          {/* Value Proposition */}
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-accent" />
                <span>2-Minute Analysis vs. 3-7 Days with Lawyers</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-accent" />
                <span>Satisfaction Guarantee</span>
              </div>
            </div>
            <Button variant="link" asChild className="text-accent">
              <Link to="/pricing">View full pricing comparison →</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section — first question pre-expanded */}
      <section id="faq" className="py-16 lg:py-24 bg-background border-t border-border/50">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            className="text-center mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge variant="secondary" className="mb-4 px-4 py-1.5">
              <FileText className="h-3 w-3 mr-1" />
              {t('faq.commonQuestions')}
            </Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4">
              {t('faq.title')}
            </h2>
            <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('faq.description')}
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              { questionKey: 'faq.q1', answerKey: 'faq.a1' },
              { questionKey: 'faq.q2', answerKey: 'faq.a2' },
              { questionKey: 'faq.q3', answerKey: 'faq.a3' },
              { questionKey: 'faq.q4', answerKey: 'faq.a4' },
              { questionKey: 'faq.q5', answerKey: 'faq.a5' },
              { questionKey: 'faq.q6', answerKey: 'faq.a6' },
              { questionKey: 'faq.q7', answerKey: 'faq.a7' },
              { questionKey: 'faq.q8', answerKey: 'faq.a8' },
            ].map((faq, index) => (
              <motion.div
                key={faq.questionKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="p-5 lg:p-6 border-border/50 hover:border-accent/30 transition-all duration-300 hover:shadow-lg">
                  <details className="group" open={index === 0}>
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <h3 className="font-semibold text-base lg:text-lg pr-4">{t(faq.questionKey)}</h3>
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center group-open:bg-accent/10 transition-colors">
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-open:text-accent group-open:rotate-90 transition-transform duration-200" />
                      </div>
                    </summary>
                    <p className="mt-4 text-muted-foreground leading-relaxed text-sm lg:text-base">
                      {t(faq.answerKey)}
                    </p>
                  </details>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* More Questions CTA */}
          <motion.div
            className="text-center mt-10 lg:mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-muted-foreground mb-4">{t('faq.stillHaveQuestions')}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" asChild className="rounded-xl">
                <Link to="/faqs">{t('faq.viewAllFaqs')}</Link>
              </Button>
              <Button variant="outline" asChild className="rounded-xl">
                <Link to="/contact">{t('faq.contactSupport')}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA — reconciled social proof */}
      <section className="py-16 sm:py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Card className="p-6 sm:p-10 lg:p-16 text-center space-y-6 sm:space-y-8 max-w-4xl mx-auto bg-gradient-to-br from-primary via-primary to-[hsl(217,91%,25%)] text-white border-0 shadow-2xl overflow-hidden relative rounded-2xl sm:rounded-3xl">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.2),transparent_50%)]" />
                <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.15),transparent_50%)]" />
              </div>
              
              <div className="relative z-10 space-y-4 sm:space-y-6">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 sm:px-4 py-1.5 sm:py-2">
                  <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm font-medium">Join 10,000+ Athletes Worldwide</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold">{t('landing.readyToTakeControl')}</h2>
                <p className="text-base sm:text-xl opacity-80 max-w-2xl mx-auto leading-relaxed px-2">
                  {t('landing.finalCtaDescription')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2 sm:pt-4">
                  <Button 
                    size="lg" 
                    asChild 
                    className="bg-white text-primary hover:bg-white/90 text-sm sm:text-base px-6 sm:px-8 h-12 sm:h-14 rounded-xl shadow-lg group w-full sm:w-auto"
                  >
                    <Link to="/auth">
                      {t('landing.startFreeAnalysis')}
                      <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 sm:py-16 lg:py-20 bg-muted">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12">
            <div className="col-span-2 sm:col-span-2 lg:col-span-1">
              <Link to="/" className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
                <img src={logo} alt="Sports Deal Shield" className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl shadow-lg object-contain" />
                <span className="font-bold text-lg sm:text-xl text-foreground">Sports Deal Shield</span>
              </Link>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-3 sm:mb-4">
                {t('landing.footerTagline')}
              </p>
              <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                <Badge variant="outline" className="text-muted-foreground border-border text-[10px] sm:text-xs px-2 py-0.5">NBA</Badge>
                <Badge variant="outline" className="text-muted-foreground border-border text-[10px] sm:text-xs px-2 py-0.5">NFL</Badge>
                <Badge variant="outline" className="text-muted-foreground border-border text-[10px] sm:text-xs px-2 py-0.5">NHL</Badge>
                <Badge variant="outline" className="text-muted-foreground border-border text-[10px] sm:text-xs px-2 py-0.5">MLB</Badge>
                <Badge variant="outline" className="text-muted-foreground border-border text-[10px] sm:text-xs px-2 py-0.5">MLS</Badge>
                <Badge variant="outline" className="text-muted-foreground border-border text-[10px] sm:text-xs px-2 py-0.5">Tennis</Badge>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4 sm:mb-5 text-sm sm:text-base text-foreground">{t('landing.footerProduct')}</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground transition-colors">{t('landing.features')}</a></li>
                <li><a href="#how-it-works" className="hover:text-foreground transition-colors">{t('landing.howItWorks')}</a></li>
                <li><a href="#pricing" className="hover:text-foreground transition-colors">{t('landing.pricing')}</a></li>
                <li><Link to="/auth" className="hover:text-foreground transition-colors">{t('common.getStarted')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 sm:mb-5 text-sm sm:text-base text-foreground">{t('landing.footerCompany')}</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-foreground transition-colors">{t('landing.aboutUs')}</Link></li>
                <li><Link to="/privacy" className="hover:text-foreground transition-colors">{t('landing.privacyPolicy')}</Link></li>
                <li><Link to="/terms" className="hover:text-foreground transition-colors">{t('landing.termsOfService')}</Link></li>
                <li><Link to="/disclaimer" className="hover:text-foreground transition-colors">{t('landing.legalDisclaimer')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 sm:mb-5 text-sm sm:text-base text-foreground">{t('landing.footerSupport')}</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                <li><Link to="/help" className="hover:text-foreground transition-colors">{t('landing.helpCenter')}</Link></li>
                <li><Link to="/faqs" className="hover:text-foreground transition-colors">{t('landing.faqs')}</Link></li>
                <li><Link to="/contact" className="hover:text-foreground transition-colors">{t('landing.contactUs')}</Link></li>
                <li><a href="mailto:support@sportsdealshield.com" className="hover:text-foreground transition-colors break-all">support@sportsdealshield.com</a></li>
              </ul>
            </div>
          </div>
          <motion.div 
            className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-border text-center text-xs sm:text-sm text-muted-foreground space-y-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <motion.p 
              className="flex items-center justify-center gap-2 bg-warning/10 py-2 px-4 rounded-lg inline-flex mx-auto"
              initial={{ scale: 0.95 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            >
              <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 animate-pulse" />
              <span><strong>{t('common.error') === 'Error' ? 'Important' : t('common.error')}:</strong> {t('landing.importantDisclaimer')}</span>
            </motion.p>
            <p>© 2026 Sports Deal Shield. {t('landing.allRightsReserved')}</p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
